import { type ReactNode, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { apiFetch } from "@/lib/apiFetch";
import { formatRunTime } from "@/lib/formatRunTime";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

type Answer = {
  id: number; parent_id: number | null; project_id: number; prompt: string; status: string;
  stage: string; total_documents: number; ready_documents: number; created_at: string;
  finished_at?: string | null;
  total_sections: number; completed_sections: number;
  children?: Answer[];
  report?: string; error_message?: string; settings?: {
    source_mode?: string; phase_total?: number; phase_completed?: number; reused_batches?: number;
  };
};
type Page = { data: Answer[]; current_page: number; last_page: number };
const active = (status: string) => ["queued", "processing"].includes(status);
const statusLabel = (status: string) => status === "cancelled" ? "Paused" : status;
const stages: Record<string, string> = {
  checking_text: "Checking project text", uploading: "Uploading new text", indexing: "Preparing searchable text",
  answering: "Writing answer", finished: "Finished", retired: "Retired analysis",
  reviewing: "Examining all project text", reconciling: "Combining findings",
};
function ancestors(items: Answer[], id: number): number[] | null {
  for (const item of items) {
    if (item.id === id) return [];
    const path = ancestors(item.children || [], id);
    if (path !== null) return [item.id, ...path];
  }
  return null;
}
async function mutate<T>(path: string, body: Record<string, unknown> = {}): Promise<T> {
  await fetch("/sanctum/csrf-cookie", { credentials: "include" });
  return apiFetch<T>(path, { method: "POST", body });
}

export default function ProjectAnalyses({ projectId = "all", projectNames = {} }: { projectId?: string; projectNames?: Record<string, string> }) {
  const [params, setParams] = useSearchParams();
  const selected = Number(params.get("analysis")) || null;
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [listing, setListing] = useState<Page | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const [run, setRun] = useState<Answer | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState("");
  const requestKey = useRef<{ parent: number; prompt: string; key: string } | null>(null);
  const isFullReview = run?.settings?.source_mode === "project-full-review-v1";
  const isSearch = isFullReview || run?.settings?.source_mode === "project-file-search-v1";
  const runTime = run ? formatRunTime(run.created_at, run.finished_at) : null;
  useEffect(() => { setPage(1); }, [projectId]);
  const select = (id: number | null) => setParams(previous => {
    const next = new URLSearchParams(previous);
    if (id) next.set("analysis", String(id)); else next.delete("analysis");
    return next;
  });

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const result = await apiFetch<Page>(`/api/project-analyses?tree=1&page=${page}${projectId === "all" ? "" : `&project_id=${encodeURIComponent(projectId)}`}`);
        if (!stopped) setListing(result);
      } catch (e) { if (!stopped) setError(e instanceof Error ? e.message : "Could not load answers"); }
      finally { if (!stopped) timer = setTimeout(refresh, 10000); }
    };
    refresh();
    return () => { stopped = true; clearTimeout(timer); };
  }, [page, projectId, revision]);

  useEffect(() => {
    if (!selected || !listing) return;
    const path = ancestors(listing.data, selected);
    if (path?.length) setExpanded(previous => {
      if (path.every(id => previous.has(id))) return previous;
      return new Set([...previous, ...path]);
    });
  }, [selected, listing]);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    setRun(null); setPrompt(""); setError("");
    if (!selected) return;
    const refresh = async () => {
      try {
        const result = await apiFetch<Answer>(`/api/project-analyses/${selected}`);
        if (!stopped) {
          setRun(result);
          if (active(result.status)) timer = setTimeout(refresh, 3000);
        }
      } catch (e) { if (!stopped) setError(e instanceof Error ? e.message : "Could not load answer"); }
    };
    refresh();
    return () => { stopped = true; clearTimeout(timer); };
  }, [selected, revision]);

  async function refine() {
    if (!selected || !prompt.trim()) return;
    setBusy(true); setError("");
    const text = prompt.trim();
    if (requestKey.current?.parent !== selected || requestKey.current?.prompt !== text) {
      requestKey.current = { parent: selected, prompt: text, key: crypto.randomUUID() };
    }
    try {
      const result = await mutate<{ id: number }>(`/api/project-analyses/${selected}/refine`, { prompt: text, request_key: requestKey.current.key });
      setExpanded(previous => new Set([...previous, ...(ancestors(listing?.data || [], selected) || []), selected]));
      requestKey.current = null; select(result.id); setRevision(v => v + 1);
    } catch (e) { setError(e instanceof Error ? e.message : "Could not queue follow-up"); }
    finally { setBusy(false); }
  }

  async function control(action: "cancel" | "retry") {
    setBusy(true); setError("");
    try { await mutate(`/api/project-analyses/${selected}/${action}`); setRevision(v => v + 1); }
    catch (e) { setError(e instanceof Error ? e.message : "Action failed"); }
    finally { setBusy(false); }
  }

  function download() {
    if (!run?.report) return;
    const url = URL.createObjectURL(new Blob([run.report], { type: "text/plain;charset=utf-8" }));
    const link = document.createElement("a"); link.href = url; link.download = `project-answer-${run.id}.txt`;
    link.click(); URL.revokeObjectURL(url);
  }

  async function deleteReport() {
    if (!run || run.status !== "cancelled" || !window.confirm(
      `Permanently delete report #${run.id} and its saved processing records? This cannot be undone. Follow-up reports, project documents, and findings used by other reports will be kept.`
    )) return;
    setBusy(true); setError("");
    try {
      await fetch("/sanctum/csrf-cookie", { credentials: "include" });
      await apiFetch(`/api/project-analyses/${run.id}`, { method: "DELETE" });
      select(null); setRun(null); setPage(1); setRevision(v => v + 1);
    } catch (e) { setError(e instanceof Error ? e.message : "Could not delete report"); }
    finally { setBusy(false); }
  }

  function renderAnswer(item: Answer, depth = 0): ReactNode {
    const children = item.children || [];
    const open = expanded.has(item.id);
    return <li key={item.id}>
      <div className="border-t py-3 grid grid-cols-[1fr_3fr_1fr] gap-3 text-sm">
        <span className="break-words text-muted-foreground">{depth === 0
          ? projectNames[String(item.project_id)] || `Project #${item.project_id}`
          : "Follow-up"}</span>
        <div className="min-w-0 flex items-start gap-2">
          {children.length > 0 ? <button type="button"
            className="shrink-0 rounded p-1 hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
            aria-expanded={open} aria-controls={`analysis-children-${item.id}`}
            aria-label={`${open ? "Collapse" : "Expand"} follow-ups to ${item.prompt}`}
            onClick={() => setExpanded(previous => {
              const next = new Set(previous);
              if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
              return next;
            })}>
            <ChevronRight aria-hidden="true" className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`} />
          </button> : <span className="w-6 shrink-0" aria-hidden="true" />}
          <div className="min-w-0">
            <button type="button" className="text-primary text-left underline break-words" onClick={() => select(item.id)}>{item.prompt}</button>
            <p className="text-xs text-muted-foreground">#{item.id} · {item.created_at}
              {children.length > 0 && ` · ${children.length} follow-up${children.length === 1 ? "" : "s"}`}</p>
          </div>
        </div>
        <span className="break-words">{statusLabel(item.status)} · {stages[item.stage] || item.stage}</span>
      </div>
      {children.length > 0 && <ul id={`analysis-children-${item.id}`} hidden={!open}
        aria-label={`Follow-ups to ${item.prompt}`} className="ml-3 border-l-2 pl-3 sm:ml-6 sm:pl-4">
        {open && children.map(child => renderAnswer(child, depth + 1))}
      </ul>}
    </li>;
  }

  return <section className="space-y-3 border rounded-lg p-4">
    <h2 className="text-xl font-semibold">Project text answers</h2>
    <p className="text-sm text-muted-foreground">Full reviews examine every processed text batch before answering. Completed work is saved for retries and matching questions in later sessions.</p>
    {error && <p role="alert" className="text-red-600">{error}</p>}
    {!listing && !error && <p>Loading answers…</p>}
    {listing?.data.length === 0 && <p>No project answers yet. Start from General AI Queries.</p>}
    {!!listing?.data.length && <div className="grid grid-cols-[1fr_3fr_1fr] gap-3 font-medium"><span>Project</span><span>Prompt</span><span>Status</span></div>}
    <ul aria-label="Project questions and follow-ups">{listing?.data.map(item => renderAnswer(item))}</ul>
    {listing && listing.last_page > 1 && <div className="flex items-center gap-3">
      <Button variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
      <span>Page {listing.current_page} of {listing.last_page}</span>
      <Button variant="outline" disabled={page >= listing.last_page} onClick={() => setPage(p => p + 1)}>Next</Button>
    </div>}
    <Dialog open={selected !== null} onOpenChange={open => { if (!open) select(null); }}>
      <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Project answer #{selected}</DialogTitle><DialogDescription>{run?.prompt || "Loading…"}</DialogDescription></DialogHeader>
        {error && <p role="alert" className="text-red-600">{error}</p>}
        {run && <>
          <p className="font-medium">Project: {projectNames[String(run.project_id)] || `Project #${run.project_id}`}</p>
          <p>{statusLabel(run.status)} · {stages[run.stage] || run.stage}</p>
          {run.status === "completed" && <p className="text-sm text-muted-foreground">
            Total run time: {runTime ?? "Unavailable"}
            {runTime && <span className="block text-xs">From submission to completion, including queue and retry waits.</span>}
          </p>}
          {isFullReview && active(run.status) && <div className="space-y-1">
            <p>{run.ready_documents}/{run.total_documents} documents examined · {run.completed_sections}/{run.total_sections} text batches completed.</p>
            {run.stage === "reconciling" && <p>{run.settings?.phase_completed || 0}/{run.settings?.phase_total || 0} groups combined in this pass.</p>}
            {!!run.settings?.reused_batches && <p>{run.settings.reused_batches} completed batches reused.</p>}
            <p>Large projects can take minutes to hours. You can leave this page and return later.</p>
          </div>}
          {!isFullReview && isSearch && <p className="text-sm text-muted-foreground">Earlier retrieval-based search. A new question or follow-up starts a full review.</p>}
          {!isFullReview && isSearch && active(run.status) && <p>{run.ready_documents}/{run.total_documents} documents searchable. You can leave this page while the answer is prepared.</p>}
          {!isSearch && <p className="text-sm text-muted-foreground">Saved historical analysis. Start a new project text search for another answer.</p>}
          {run.error_message && <p className="text-red-600">{run.error_message}</p>}
          <div className="flex flex-wrap gap-2">
            {isSearch && active(run.status) && <Button variant="outline" disabled={busy} onClick={() => control("cancel")}>Pause Report</Button>}
            {isSearch && ["failed", "cancelled"].includes(run.status) && <Button disabled={busy} onClick={() => control("retry")}>{run.status === "cancelled" ? "Resume Report" : "Retry"}</Button>}
            {run.status === "cancelled" && <Button variant="destructive" disabled={busy} onClick={deleteReport}>Delete Report</Button>}
            {run.report && <Button variant="outline" onClick={download}>Download answer</Button>}
          </div>
          <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{run.report || "The answer will appear here when ready."}</div>
          {isSearch && run.status === "completed" && <div className="border-t pt-4 space-y-3">
            <h3 className="font-semibold">Ask a follow-up</h3>
            <p className="text-sm text-muted-foreground">Examines the current processed project text for your follow-up. A changed question may require another full pass. Your previous answer is retained.</p>
            <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} maxLength={6000} placeholder="What would you like to know next?" />
            <Button disabled={busy || !prompt.trim()} onClick={refine}>{busy ? "Queuing…" : "Ask follow-up"}</Button>
          </div>}
        </>}
      </DialogContent>
    </Dialog>
  </section>;
}
